import { createSelector } from 'reselect';

import {
  selectCurrentLevel,
  selectCurrentDealerId,
} from 'app/dealer/shared/modules/dealersInsights/redux/level/selectors';
import { dealerInsightsDefaultCacheKey } from 'app/dealer/shared/modules/dealersInsights/api/cache';

import { getListingsBenchmarkReducer } from '../selectors';
import { selectSelectedListingsBenchmarkTimeDivisionFilter } from '../timeDivision/selectors';
import {
  selectListingsBenchmarkSelectedYear,
  selectListingsBenchmarkSelectedMonth,
} from '../general/selectors';
import { topListingsBenchmarkInitialState } from '../reducer.initialData';

import { TOP_LISTINGS_CACHE_PREFIX } from './constants';

/**
 *
 * @param {import('../reducer.defaultState').TimeDivision} selectedTimeDivisionFilter
 * @param {number} selectedYear
 * @param {number} selectedMonth
 * @param {import('../../../../../shared/modules/dealersInsights/redux/level/reducer.defaultState').DealerInsightsLevel} level
 * @param {string} dealerId
 */
export const getTopListingsBenchmarkCacheDictionaryKey = (
  selectedTimeDivisionFilter,
  selectedYear,
  selectedMonth,
  level,
  dealerId,
) =>
  dealerInsightsDefaultCacheKey({
    featurePrefix: TOP_LISTINGS_CACHE_PREFIX,
    selectedTimeDivisionFilter,
    selectedYear,
    selectedMonth,
    level,
    dealerId,
  });

// #region select data by date and time division filters: main selector
/**
 * @description
 * main selector for selecting data corresponding to
 * cached dates in the redux store
 */
export const selectTopListingsBenchmarkDataByDate = createSelector(
  [
    getListingsBenchmarkReducer,
    selectSelectedListingsBenchmarkTimeDivisionFilter,
    selectListingsBenchmarkSelectedYear,
    selectListingsBenchmarkSelectedMonth,
    selectCurrentLevel,
    selectCurrentDealerId,
  ],
  (state, selectedTimeDivisionFilter, year, month, level, dealerId) =>
    state[
      getTopListingsBenchmarkCacheDictionaryKey(
        selectedTimeDivisionFilter,
        year,
        month,
        level,
        dealerId,
      )
    ] || topListingsBenchmarkInitialState,
);
// #endregion select data by date and time division filters: main selector

export const selectTopListingsBenchmarkByFavourites = createSelector(
  [selectTopListingsBenchmarkDataByDate],
  ({ favs = [] }) => favs,
);

export const selectTopListingsBenchmarkByViews = createSelector(
  [selectTopListingsBenchmarkDataByDate],
  ({ clicks = [] }) => clicks,
);

export const selectTopListingsBenchmarkByLeads = createSelector(
  [selectTopListingsBenchmarkDataByDate],
  ({ leads = [] }) => leads,
);

// #region data properties: all are empty
export const selectAllTopListingsBenchmarkDataValuesAreEmpty = createSelector(
  [
    selectTopListingsBenchmarkByFavourites,
    selectTopListingsBenchmarkByViews,
    selectTopListingsBenchmarkByLeads,
  ],
  (favourites, views, leads) =>
    favourites.length === 0 && views.length === 0 && leads.length === 0,
);
// #endregion data properties: all are zero

// #region data properties: top listings count
export const selectSelectedTopListingsCount = createSelector(
  [getListingsBenchmarkReducer],
  ({ selectedTopListingsCount }) => selectedTopListingsCount,
);
// #endregion data properties: top listings count

export const selectSelectedTopListingsCategory = createSelector(
  [getListingsBenchmarkReducer],
  ({ selectedTopListingsCategory }) => selectedTopListingsCategory,
);
